import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { catchError, map } from 'rxjs/operators';
import { readQueryParameters } from '../shared-functions';
import { Country, ICompany, IEnterprise } from '../models/company.model';
import { TranslateService } from '@ngx-translate/core';
import { IApplication } from '../models/application.model';
import { Application } from '../classes/application.class';
import { Pagination } from '../models/pagination.interface';
import { Enterprise, Company, Organization, AllJobsOrganization, ALL_JOBS_DOMAIN } from '../classes/organization.class';
import { ErrorHandlingService } from './error-handling.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from './loader.service';

@Injectable({
  providedIn: 'root',
})
export class SetupService {

  protected _domain: string;
  protected _isEnterprise = false;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService,
    private toastr: ToastrService,
    private loaderService: LoaderService,
    protected translateService: TranslateService,
  ) {}

  getOrganizationInfo(): Observable<Organization> {
    this.setDomain();

    if (this._domain === ALL_JOBS_DOMAIN) {
      let params = new HttpParams();
      params = params.set('limit', 1000);
      return this.http.get(`${environment.countries}`, { params })
        .pipe(
          map(({data}: Pagination<Country>) => {
            //only Sweden is allowed in Casting
            const sweden = data.filter((country: Country) => country.code === 'SE');
            return new AllJobsOrganization(sweden);
          })
        );
    }

    if (this._isEnterprise) {
      let params = new HttpParams();
      params = params.set('enterpriseDomain', this._domain);

      return this.http.get(`${environment.enterprises}`, { params })
        .pipe(
          map(({data}: Pagination<IEnterprise>) => {
            return data[0] ? new Enterprise(data[0]) : null;
          })
        );
    }

    return this.http.get(`${environment.companyInfo}/${this._domain}`)
      .pipe(
        map(({data}: Pagination<ICompany>) => {
          return data[0] ? new Company(data[0]) : null;
        })
      );
  }

  setDomain(): void {
    const { host, hostname } = window.location;

    if (hostname === 'localhost' || hostname.includes('192.168.') || hostname.includes('bs-local')) {
      // this._domain = ALL_JOBS_DOMAIN;
      this._domain = 'casting';
      // this._domain = 'castingent';
      // this._isEnterprise = true;
      return;
    }

    const domain: string[] = hostname.split('.');

    if (domain[0] === 'www') {
      domain.shift();
    }

    if (domain[1] === 'global') {
      this._isEnterprise = true;
    }

    const higherIndex = domain.findIndex((domainItem) => domainItem === 'higher' || domainItem === 'uhigher');
    if (higherIndex !== 1 && higherIndex === 2 && !this._isEnterprise) {
      window.location.href = 'https://www.uhigher.se';
    }

    this._domain = domain[0];
  }

  getApplicationInfo(applicationGuid?: string): Observable<Application> {
    const guid = applicationGuid || readQueryParameters('application');

    return this.http.get(`${environment.applications}/${guid}/for-candidate-portal?context=candidate_journey`)
      .pipe(
        map((data: IApplication) => new Application(data)),
        catchError((errorResponse: HttpErrorResponse) => {
          if (errorResponse.status === 404) {
            this.toastr.error(this.translateService.instant('SHARED.TOAST_ERROR_SMT_WENT_WRONG'));
            this.loaderService.hide();
            return EMPTY;
          }
          return this.errorHandlingService.handleBackendError(errorResponse)
        }
        )
      );
  }

  getJobUpdateApplicationInfo(): Observable<Application> {
    const guid = readQueryParameters('application');

    return this.http.get(`${environment.applications}/${guid}/for-candidate-portal?context=candidate_edit_selected_answers_via_reminder_link`)
      .pipe(
        map((data: IApplication) => new Application(data))
      );
  }
}
